"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { RiAlertLine, RiCheckboxCircleLine, RiGitBranchLine, RiMessageLine } from "react-icons/ri";
import { Skeleton } from "@/components/ui/skeleton";
import { BaseStepCard } from "@/components/ui/step-card";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { getCycleColor } from "@/lib/care-plan-cycle-colors";
import { formatDate, formatToHumanReadable } from "@/lib/format-data";
import { hydrateSmsTemplateFromSuggestions } from "@/lib/sms-templates";
import { cx } from "@/lib/tailwind-utils";
import type { CarePlanCycleDetail } from "@/types/care-plan";
import { useCarePlanContext } from "./care-plan";
import { CycleBadge, TemplateCycleBadge } from "./cycle-badge";
import { useOptionalBranch } from "./decision/branch";
import { useCarePlanPrint } from "./print/care-plan-print-context";
import { StepSequences, StepSequencesProvider } from "./sequences";
import { StepSummaryTags } from "./step-summary-tags";
import { type CarePlanStep, CarePlanStepStatus, type CycleCreationState, type SequenceCopyState, StepType, getStepDisplayName, isLinkedMessage, isPatientStep } from "./types";
import { useSuggestions } from "@/api/query/provider/suggestions";

// ============================================================================
// Pure helpers — extracted from inline logic for readability
// ============================================================================

/** Walk up the parent chain to collect ancestor cycle badges. */
function getAncestorBadges(cycleId: string, allCycles: CarePlanCycleDetail[], cycleToParentPass: Map<string, number>): {
  cycleId: string;
  pass: number;
}[] {
  const badges: {
    cycleId: string;
    pass: number;
  }[] = [];
  let currentId = cycleId;
  let current = allCycles.find(c => c.id === currentId);
  while (current?.parentCycleId) {
    badges.unshift({
      cycleId: current.parentCycleId,
      pass: cycleToParentPass.get(currentId) ?? 1
    });
    currentId = current.parentCycleId;
    current = allCycles.find(c => c.id === currentId);
  }
  return badges;
}

/** Derive all cycle-creation visual flags for a step. */
function getCycleCreationFlags(step: CarePlanStep, cycleCreation: CycleCreationState | null, originalSteps: CarePlanStep[]) {
  if (!cycleCreation) {
    return {
      isGrayed: false,
      isSelected: false,
      isInRange: false,
      isClickable: false
    };
  }
  const {
    phase,
    startStepId,
    endStepId,
    startStepIndex,
    endStepIndex
  } = cycleCreation;
  const isNonMaterialized = !(step as CarePlanStep & {
    sourceStepId?: string | null;
  }).sourceStepId && !isLinkedMessage(step);
  const originalStepIndex = originalSteps.findIndex(s => s.id === step.id);
  const isInOriginalSteps = originalStepIndex !== -1;
  const isSelectable = phase === "selecting-start" ? isNonMaterialized : isInOriginalSteps;
  const isSelected = startStepId === step.id || endStepId === step.id;
  const isInRange = phase === "configuring" && startStepIndex !== null && endStepIndex !== null && isInOriginalSteps && originalStepIndex >= startStepIndex && originalStepIndex <= endStepIndex;
  const isGrayed = !isSelectable || phase === "selecting-end" && startStepIndex !== null && isInOriginalSteps && originalStepIndex < startStepIndex || phase === "configuring" && !isInRange && !isSelected;
  const isClickable = isSelectable && !isGrayed && phase !== "configuring";
  return {
    isGrayed,
    isSelected,
    isInRange,
    isClickable
  };
}

/**
 * Derive sequence-copy visual flags for a step. Mirrors `getCycleCreationFlags`
 * — but the rules are simpler: only original (non-materialized) APPOINTMENT
 * steps qualify as targets; the source step is highlighted but unclickable.
 */
function getSequenceCopyFlags(step: CarePlanStep, copy: SequenceCopyState | null) {
  if (!copy) {
    return {
      isSource: false,
      isTarget: false,
      isClickable: false,
      isGrayed: false
    };
  }
  const isMaterializedClone = !!(step as CarePlanStep & {
    sourceStepId?: string | null;
  }).sourceStepId;
  const isAppointment = step.type === StepType.APPOINTMENT;
  const isLinked = isLinkedMessage(step);
  const isSource = step.id === copy.sourceStepId;
  const isTarget = copy.targetStepIds.includes(step.id);
  const isEligible = isAppointment && !isMaterializedClone && !isLinked;
  const isClickable = isEligible && !isSource;
  const isGrayed = !isEligible && !isSource;
  return {
    isSource,
    isTarget,
    isClickable,
    isGrayed
  };
}

/** Compute the 1-based display number (0 for linked messages/decisions). */
function getDisplayNumber(step: CarePlanStep, stepsForNumbering: CarePlanStep[]) {
  if (isLinkedMessage(step) || step.type === StepType.DECISION) return 0;
  return stepsForNumbering.filter(s => !isLinkedMessage(s) && s.type !== StepType.DECISION).findIndex(s => s.id === step.id) + 1;
}

// ============================================================================
// Step icon badge (top-left of card)
// ============================================================================

const iconBadge = "aspect-square w-4 shrink-0 flex items-center justify-center rounded-md";
function StepIcon(t0) {
  const $ = _c(6);
  const {
    step,
    displayNumber
  } = t0;
  switch (step.type) {
    case StepType.MESSAGE:
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <span className={cx(iconBadge, "bg-primary text-tiny text-primary-foreground")}><RiMessageLine size={10} /></span>;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case StepType.DECISION:
      {
        let t1;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <span className={cx(iconBadge, "bg-status-warning-bg text-status-warning-text")}><RiGitBranchLine size={10} /></span>;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case StepType.TASK:
      {
        let t1;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <span className={cx(iconBadge, "bg-primary text-tiny text-primary-foreground")}><RiCheckboxCircleLine size={10} /></span>;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    default:
      {
        let t1;
        if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = cx(iconBadge, "bg-primary text-tiny font-semibold text-primary-foreground");
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        let t2;
        if ($[4] !== displayNumber) {
          t2 = <span className={t1}>{displayNumber}</span>;
          $[4] = displayNumber;
          $[5] = t2;
        } else {
          t2 = $[5];
        }
        return t2;
      }
  }
}

// ============================================================================
// StepCard
// ============================================================================

interface StepCardProps {
  step: CarePlanStep;
  isDragging?: boolean;
}
export function StepCard(t0) {
  const $ = _c(126);
  const {
    step,
    isDragging
  } = t0;
  const printing = useCarePlanPrint();
  const clock = useClinicClock();
  const {
    state: t1,
    actions: t2,
    config: t3,
    lookups: t4,
    cycles: t5,
    sequenceCopy: t6
  } = useCarePlanContext();
  const {
    steps,
    selectedStepId
  } = t1;
  const {
    selectStep
  } = t2;
  const {
    size
  } = t3;
  const {
    patientId,
    serviceById,
    medicationById,
    measurementById,
    isLookupsLoading
  } = t4;
  const {
    cycles: allCycles,
    cycleCreation,
    selectCycleCreationStep,
    originalSteps,
    highlightedCycleId,
    cycleFamilyMap,
    cycleColorMap,
    cycleToParentPass
  } = t5;
  const {
    state: sequenceCopy,
    toggleTarget: toggleSequenceCopyTarget
  } = t6;
  let t7;
  if ($[0] !== patientId) {
    t7 = patientId ? ["PATIENT", "TENANT"] : ["TENANT"];
    $[0] = patientId;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const {
    data: t8
  } = useSuggestions(t7, patientId);
  let t9;
  if ($[2] !== t8) {
    t9 = t8 === undefined ? [] : t8;
    $[2] = t8;
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  const mergeFieldSuggestions = t9;
  let t10;
  bb0: {
    if (!step.smsBody) {
      t10 = "";
      break bb0;
    }
    let t11;
    if ($[4] !== mergeFieldSuggestions || $[5] !== step.smsBody) {
      t11 = hydrateSmsTemplateFromSuggestions(step.smsBody, mergeFieldSuggestions);
      $[4] = mergeFieldSuggestions;
      $[5] = step.smsBody;
      $[6] = t11;
    } else {
      t11 = $[6];
    }
    t10 = t11;
  }
  const messageBodyPreview = t10;
  const branchCtx = useOptionalBranch();
  const isMessage = step.type === StepType.MESSAGE;
  const isDecision = step.type === StepType.DECISION;
  const isSelected = selectedStepId === step.id;
  const isSm = size === "sm";
  const stepsForNumbering = branchCtx ? branchCtx.branch.steps : steps;
  let t11;
  if ($[7] !== step || $[8] !== stepsForNumbering) {
    t11 = getDisplayNumber(step, stepsForNumbering);
    $[7] = step;
    $[8] = stepsForNumbering;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const displayNumber = t11;
  const isLinked = isMessage && !!step.parentStepId;
  let t12;
  if ($[10] !== isLinked || $[11] !== isMessage || $[12] !== measurementById || $[13] !== medicationById || $[14] !== serviceById || $[15] !== step) {
    t12 = isMessage && isLinked ? step.messageType ? formatToHumanReadable(step.messageType) : "Message" : getStepDisplayName(step, {
      serviceById,
      medicationById,
      measurementById
    });
    $[10] = isLinked;
    $[11] = isMessage;
    $[12] = measurementById;
    $[13] = medicationById;
    $[14] = serviceById;
    $[15] = step;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const displayName = t12;
  const hasLookupContent = step.serviceIds.length > 0 || step.medicationIds.length > 0 || step.measurements.length > 0;
  const showNameSkeleton = isLookupsLoading && !isMessage && hasLookupContent;
  let T0;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t30;
  if ($[17] !== allCycles || $[18] !== clock || $[19] !== cycleColorMap || $[20] !== cycleCreation || $[21] !== cycleFamilyMap || $[22] !== cycleToParentPass || $[23] !== displayName || $[24] !== displayNumber || $[25] !== highlightedCycleId || $[26] !== isDecision || $[27] !== isDragging || $[28] !== isMessage || $[29] !== isSelected || $[30] !== isSm || $[31] !== messageBodyPreview || $[32] !== originalSteps || $[33] !== printing || $[34] !== selectCycleCreationStep || $[35] !== selectStep || $[36] !== sequenceCopy || $[37] !== showNameSkeleton || $[38] !== size || $[39] !== step || $[40] !== toggleSequenceCopyTarget) {
    const isPatient = isPatientStep(step);
    const completedDate = isPatient && step.completedDate ? step.completedDate : undefined;
    const scheduledDate = completedDate ?? (isPatient ? step.scheduledDate : undefined);
    const isSkipped = isPatient && step.status === CarePlanStepStatus.SKIPPED;
    const isCompleted = isPatient && step.status === CarePlanStepStatus.COMPLETED;
    const isEstimated = isPatient && !!step.isDateEstimated && step.status === CarePlanStepStatus.PENDING;
    const isOverdue = isPatient && step.status === CarePlanStepStatus.PENDING && !isEstimated && !!scheduledDate && clock.dayOf(scheduledDate) < clock.today();
    let t31;
    if ($[60] !== step.branches) {
      t31 = step.branches ?? [];
      $[60] = step.branches;
      $[61] = t31;
    } else {
      t31 = $[61];
    }
    const decisionBranches = t31;
    let t32;
    if ($[62] !== decisionBranches) {
      t32 = decisionBranches.find(_temp);
      $[62] = decisionBranches;
      $[63] = t32;
    } else {
      t32 = $[63];
    }
    const selectedDecisionBranch = t32;
    const isDecisionResolved = isDecision && isCompleted;
    const cycleId = step.cycleId ?? null;
    const cycleColorIndex = cycleId ? cycleColorMap.get(cycleId) : undefined;
    const cycleColor = cycleColorIndex !== undefined ? getCycleColor(cycleColorIndex) : null;
    const ancestorBadges = cycleId ? getAncestorBadges(cycleId, allCycles, cycleToParentPass) : [];
    const isInCycleCreation = !!cycleCreation;
    const cycle = getCycleCreationFlags(step, cycleCreation, originalSteps);
    const isInSequenceCopy = !!sequenceCopy;
    const copy = getSequenceCopyFlags(step, sequenceCopy);
    const cycleChain = cycleId ? [cycleId, ...ancestorBadges.map(_temp2)] : [];
    const isCycleHighlightDimmed = !isInCycleCreation && !!highlightedCycleId && !cycleChain.some(id => {
      const family = cycleFamilyMap.get(id);
      return family ? family.has(highlightedCycleId) : id === highlightedCycleId;
    });
    const handleClick = () => {
      if (copy.isClickable) {
        toggleSequenceCopyTarget(step.id);
        return;
      }
      if (isInSequenceCopy) {
        return;
      }
      if (cycle.isClickable) {
        selectCycleCreationStep(step.id);
        return;
      }
      if (isInCycleCreation) {
        return;
      }
      selectStep(isSelected ? null : step.id);
    };
    const t33 = isSm ? "w-min" : "w-72";
    if ($[64] !== t33) {
      t28 = cx("group relative", t33);
      $[64] = t33;
      $[65] = t28;
    } else {
      t28 = $[65];
    }
    t29 = true;
    t30 = step.id;
    T0 = BaseStepCard;
    t23 = size;
    t24 = !isInCycleCreation && !isInSequenceCopy && isSelected;
    t25 = handleClick;
    t26 = cx("relative flex flex-col", "pointer-events-auto", isDragging && "z-10 opacity-70 shadow-lg", isMessage && !cycleColor && "border-l-2 border-l-accent-foreground", cycleColor && "border-l-4", isCycleHighlightDimmed && "opacity-30 transition-opacity", cycle.isGrayed && "opacity-40 pointer-events-none", cycle.isSelected && "ring-2 ring-primary border-primary bg-primary/5", cycle.isInRange && !cycle.isSelected && "border-primary/40 bg-primary/5", cycle.isClickable && "cursor-pointer hover:border-primary hover:ring-1 hover:ring-primary/50 hover:bg-primary/5", copy.isGrayed && "opacity-40 pointer-events-none", copy.isSource && "ring-2 ring-status-warning-text border-status-warning-text bg-status-warning-bg/20", copy.isTarget && "ring-2 ring-primary border-primary bg-primary/10", copy.isClickable && !copy.isTarget && "cursor-pointer hover:border-primary hover:ring-1 hover:ring-primary/50 hover:bg-primary/5", !isInCycleCreation && !isSelected && !displayName && !isMessage && "border-dashed border-foreground-muted");
    if ($[66] !== cycleColor) {
      t27 = cycleColor ? {
        borderLeftColor: cycleColor.border
      } : undefined;
      $[66] = cycleColor;
      $[67] = t27;
    } else {
      t27 = $[67];
    }
    t13 = "min-w-0 text-left";
    t14 = true;
    let t34;
    if ($[68] !== displayNumber || $[69] !== step) {
      t34 = <StepIcon step={step} displayNumber={displayNumber} />;
      $[68] = displayNumber;
      $[69] = step;
      $[70] = t34;
    } else {
      t34 = $[70];
    }
    const t35 = showNameSkeleton ? <Skeleton className="h-4 w-32" /> : <span className={cx("truncate font-medium", isSm ? "text-caption" : "text-body", !displayName ? "text-foreground-muted" : "text-foreground", isSkipped && "line-through text-foreground-secondary")}>{displayName || "Untitled Step"}</span>;
    if ($[71] !== t34 || $[72] !== t35) {
      t15 = <div className="flex items-start gap-1.5" data-care-plan-card-title={true}>{t34}{t35}</div>;
      $[71] = t34;
      $[72] = t35;
      $[73] = t15;
    } else {
      t15 = $[73];
    }
    if ($[74] !== isCompleted || $[75] !== isEstimated || $[76] !== isOverdue || $[77] !== printing || $[78] !== scheduledDate) {
      t16 = !!scheduledDate && <p data-care-plan-card-date={true} className={cx("mt-1 text-caption text-left flex items-center gap-1", isEstimated ? "text-foreground-muted" : isOverdue ? "text-status-error-text font-medium" : isCompleted ? "text-status-success-text font-medium" : "text-foreground-secondary")} title={isEstimated ? "Estimated \u2014 will update when the previous appointment completes" : undefined}>{(isOverdue || isCompleted || printing) && <span data-care-plan-date-icon={true} className="aspect-square w-3 shrink-0 flex items-center justify-center">{isOverdue && <RiAlertLine size={12} />}{isCompleted && <RiCheckboxCircleLine size={12} />}</span>}<span><span>{isEstimated ? "~" : ""}</span>{formatDate(scheduledDate)}</span></p>;
      $[74] = isCompleted;
      $[75] = isEstimated;
      $[76] = isOverdue;
      $[77] = printing;
      $[78] = scheduledDate;
      $[79] = t16;
    } else {
      t16 = $[79];
    }
    if ($[80] !== isMessage || $[81] !== messageBodyPreview || $[82] !== step.smsBody) {
      t17 = isMessage && step.smsBody && <p className="mt-1.5 text-caption text-foreground-secondary line-clamp-2">{messageBodyPreview}</p>;
      $[80] = isMessage;
      $[81] = messageBodyPreview;
      $[82] = step.smsBody;
      $[83] = t17;
    } else {
      t17 = $[83];
    }
    if ($[84] !== isDecision || $[85] !== isDecisionResolved || $[86] !== selectedDecisionBranch) {
      t18 = isDecision && isDecisionResolved && selectedDecisionBranch?.name && <p className="mt-1 text-caption text-status-success-text truncate">Selected: {selectedDecisionBranch.name}</p>;
      $[84] = isDecision;
      $[85] = isDecisionResolved;
      $[86] = selectedDecisionBranch;
      $[87] = t18;
    } else {
      t18 = $[87];
    }
    if ($[88] !== decisionBranches.length || $[89] !== isDecision || $[90] !== isDecisionResolved || $[91] !== isSkipped) {
      t19 = isDecision && !isDecisionResolved && !isSkipped && decisionBranches.length > 0 && <p className="mt-1 text-caption text-foreground-muted">{decisionBranches.length} option<span>{decisionBranches.length !== 1 ? "s" : ""}</span> — awaiting selection</p>;
      $[88] = decisionBranches.length;
      $[89] = isDecision;
      $[90] = isDecisionResolved;
      $[91] = isSkipped;
      $[92] = t19;
    } else {
      t19 = $[92];
    }
    if ($[93] !== displayName || $[94] !== isMessage) {
      t20 = !isMessage && !displayName && <p className="text-body text-foreground-muted my-2 text-left">Click to configure</p>;
      $[93] = displayName;
      $[94] = isMessage;
      $[95] = t20;
    } else {
      t20 = $[95];
    }
    if ($[96] !== step) {
      t21 = <StepSummaryTags step={step} className="mt-2 mb-1" />;
      $[96] = step;
      $[97] = t21;
    } else {
      t21 = $[97];
    }
    t22 = cycleColorIndex !== undefined && cycleId && <div className="mt-1 flex flex-wrap items-center gap-1">{ancestorBadges.map(t36 => {
        const {
          cycleId: aid,
          pass
        } = t36;
        const idx = cycleColorMap.get(aid);
        const definition = allCycles.find(c => c.id === aid);
        if (printing && !isPatient) {
          return idx !== undefined && definition ? <TemplateCycleBadge key={aid} cycle={definition} colorIndex={idx} /> : null;
        }
        return idx !== undefined ? <CycleBadge key={aid} cyclePass={pass} colorIndex={idx} cycleName={allCycles.find(c_0 => c_0.id === aid)?.name} /> : null;
      })}{printing && !isPatient ? allCycles.filter(c_1 => c_1.id === cycleId).map(definition_0 => <TemplateCycleBadge key={definition_0.id} cycle={definition_0} colorIndex={cycleColorIndex} />) : <CycleBadge cyclePass={typeof step.cyclePass === "number" ? step.cyclePass : 1} colorIndex={cycleColorIndex} cycleName={allCycles.find(c_2 => c_2.id === cycleId)?.name} />}</div>;
    $[17] = allCycles;
    $[18] = clock;
    $[19] = cycleColorMap;
    $[20] = cycleCreation;
    $[21] = cycleFamilyMap;
    $[22] = cycleToParentPass;
    $[23] = displayName;
    $[24] = displayNumber;
    $[25] = highlightedCycleId;
    $[26] = isDecision;
    $[27] = isDragging;
    $[28] = isMessage;
    $[29] = isSelected;
    $[30] = isSm;
    $[31] = messageBodyPreview;
    $[32] = originalSteps;
    $[33] = printing;
    $[34] = selectCycleCreationStep;
    $[35] = selectStep;
    $[36] = sequenceCopy;
    $[37] = showNameSkeleton;
    $[38] = size;
    $[39] = step;
    $[40] = toggleSequenceCopyTarget;
    $[41] = T0;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
    $[54] = t25;
    $[55] = t26;
    $[56] = t27;
    $[57] = t28;
    $[58] = t29;
    $[59] = t30;
  } else {
    T0 = $[41];
    t13 = $[42];
    t14 = $[43];
    t15 = $[44];
    t16 = $[45];
    t17 = $[46];
    t18 = $[47];
    t19 = $[48];
    t20 = $[49];
    t21 = $[50];
    t22 = $[51];
    t23 = $[52];
    t24 = $[53];
    t25 = $[54];
    t26 = $[55];
    t27 = $[56];
    t28 = $[57];
    t29 = $[58];
    t30 = $[59];
  }
  let t31;
  if ($[98] !== step.id || $[99] !== step.type) {
    t31 = step.type === StepType.APPOINTMENT && <div className="mt-2 flex items-center"><StepSequencesProvider stepId={step.id}><StepSequences.Chip /></StepSequencesProvider></div>;
    $[98] = step.id;
    $[99] = step.type;
    $[100] = t31;
  } else {
    t31 = $[100];
  }
  let t32;
  if ($[101] !== t13 || $[102] !== t14 || $[103] !== t15 || $[104] !== t16 || $[105] !== t17 || $[106] !== t18 || $[107] !== t19 || $[108] !== t20 || $[109] !== t21 || $[110] !== t22 || $[111] !== t31) {
    t32 = <div className={t13} data-care-plan-card-content={t14}>{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t31}</div>;
    $[101] = t13;
    $[102] = t14;
    $[103] = t15;
    $[104] = t16;
    $[105] = t17;
    $[106] = t18;
    $[107] = t19;
    $[108] = t20;
    $[109] = t21;
    $[110] = t22;
    $[111] = t31;
    $[112] = t32;
  } else {
    t32 = $[112];
  }
  let t33;
  if ($[113] !== T0 || $[114] !== t23 || $[115] !== t24 || $[116] !== t25 || $[117] !== t26 || $[118] !== t27 || $[119] !== t32) {
    t33 = <T0 size={t23} isSelected={t24} onClick={t25} className={t26} style={t27}>{t32}</T0>;
    $[113] = T0;
    $[114] = t23;
    $[115] = t24;
    $[116] = t25;
    $[117] = t26;
    $[118] = t27;
    $[119] = t32;
    $[120] = t33;
  } else {
    t33 = $[120];
  }
  let t34;
  if ($[121] !== t28 || $[122] !== t29 || $[123] !== t30 || $[124] !== t33) {
    t34 = <div className={t28} data-step-card={t29} data-step-id={t30}>{t33}</div>;
    $[121] = t28;
    $[122] = t29;
    $[123] = t30;
    $[124] = t33;
    $[125] = t34;
  } else {
    t34 = $[125];
  }
  return t34;
}
function _temp2(a) {
  return a.cycleId;
}
function _temp(b) {
  return !!b.selectedDate;
}